import { Outlet, createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { ApolloProvider } from '@apollo/client/react';
import * as Sentry from '@sentry/react';
import { Fragment, useEffect } from 'react';

import { Toaster } from '@/components/ui/sonner';
import { apolloClient } from 'src/graphql';
import { LayoutContainer as Layout } from 'src/containers/Layout';

import { buildProvidersTree } from './utils/buildProvidersTree';
import {
  authRoutes,
  loginRoutes,
  settingsRoutes,
  helpResourcesRoutes,
  mainRoutes,
  applicationRoutes,
  applicationSetupRoutes,
  applicationEditRoutes,
  applicationMembersRoutes,
  applicationTasksRoutes,
  applicationLinkNewRoutes,
  onboardingRoutes,
  backupSingleRoutes,
  backupOnboardingRoutes,
  vendorRequestsRoutes,
  softwareEscrowsOnboardingRoutes,
  softwareEscrowRoutes,
  softwareEscrowsRequestChangesRoutes,
  continuityEscrowsOnboardingRoutes,
  continuityEscrowRoutes,
  continuityEscrowsRequestChangesRoutes,
  saasEscrowsOnboardingRoutes,
  saasEscrowRoutes,
  saasEscrowsRequestChangesRoutes,
  custodianProfileRoutes,
  custodianProfilesOnboardingRoutes,
} from './routes';
import { AuthProvider } from './context/auth/use-auth';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import { NotFound } from './components/404';
import { ErrorBoundary, ScrollToTop } from './components/Misc';
import SessionExpiredPage from './pages/SessionExpired';
import { SessionTimeoutProvider } from './widgets/SessionTimeoutProvider';

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const router = sentryCreateBrowserRouter([
  {
    element: (
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    ),
    children: [
      { ...loginRoutes },
      { path: '/session-expired', element: <SessionExpiredPage /> },
      // All other routes wrapped in SessionTimeoutProvider
      {
        element: (
          <SessionTimeoutProvider>
            <Outlet />
          </SessionTimeoutProvider>
        ),
        children: [
          { ...authRoutes },
          { ...onboardingRoutes },
          // Protected routes with main layout
          {
            element: (
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            ),
            errorElement: <ErrorBoundary />,
            children: [
              { ...mainRoutes },
              { ...applicationRoutes },
              { ...applicationSetupRoutes },
              { ...applicationEditRoutes },
              { ...applicationMembersRoutes },
              { ...applicationTasksRoutes },
              { ...applicationLinkNewRoutes },
              { ...settingsRoutes },
              { ...helpResourcesRoutes },
              { ...backupSingleRoutes },
              { ...vendorRequestsRoutes },
              { ...softwareEscrowRoutes },
              { ...continuityEscrowRoutes },
              { ...saasEscrowRoutes },
              { ...custodianProfileRoutes },
            ],
          },
          // Protected routes with custom layout
          {
            element: (
              <ProtectedRoute>
                <Outlet />
              </ProtectedRoute>
            ),
            errorElement: <ErrorBoundary />,
            children: [
              softwareEscrowsOnboardingRoutes,
              softwareEscrowsRequestChangesRoutes,
              continuityEscrowsOnboardingRoutes,
              continuityEscrowsRequestChangesRoutes,
              saasEscrowsOnboardingRoutes,
              saasEscrowsRequestChangesRoutes,
              custodianProfilesOnboardingRoutes,
              backupOnboardingRoutes,
            ],
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
  {
    path: '404',
    element: <NotFound />,
  },
]);

const ProvidersTree = buildProvidersTree([
  [ApolloProvider, { client: apolloClient }],
  [RouterProvider, { router }],
  [ScrollToTop],
]);

const App = () => {
  // react-router crashes when there are multiple slashes in the URL
  // This is a workaround to prevent this crash, but it's not a perfect solution
  // TODO: Find a better solution later
  useEffect(() => {
    const { pathname } = window.location;

    if (pathname.match(/\/{2,}/)) window.location.replace(pathname.replace(/\/{2,}/g, '/'));
  }, []);

  return (
    <Fragment>
      <ProvidersTree />
      <Toaster
        position="top-right"
        richColors
        toastOptions={{
          style: {
            fontFamily: "'Mulish', sans-serif",
          },
          classNames: {
            toast: 'p-4! bg-white! border border-border! rounded-md!',
            title: 'text-foreground! text-sm!',
            description: 'text-muted-foreground/90! text-sm!',
          },
        }}
        duration={3000}
      />
    </Fragment>
  );
};

export default App;
